import { ReactNode, Fragment, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Menu, Transition, Dialog } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  CubeIcon,
  UserIcon,
  ArrowRightStartOnRectangleIcon,
  ShieldCheckIcon,
  BoltIcon,
  CircleStackIcon,
  WrenchScrewdriverIcon,
  ChartBarIcon,
  CodeBracketIcon,
  ArchiveBoxIcon,
  ClockIcon,
  ServerStackIcon,
  CpuChipIcon,
  Cog6ToothIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { useQuery } from 'react-query';
import { projectsApi } from '@/lib/api';
import clsx from 'clsx';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const mainNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Projects', href: '/projects', icon: CubeIcon },
];

const getProjectNavGroups = (projectId: string): NavGroup[] => [
  {
    label: 'General',
    items: [
      { name: 'Overview', href: `/projects/${projectId}`, icon: HomeIcon },
      { name: 'Query Builder', href: `/projects/${projectId}/query-builder`, icon: CpuChipIcon },
    ],
  },
  {
    label: 'Database',
    items: [
      { name: 'Schema', href: `/projects/${projectId}/schema`, icon: CircleStackIcon },
      { name: 'Migrations', href: `/projects/${projectId}/migrations`, icon: WrenchScrewdriverIcon },
      { name: 'RLS Policies', href: `/projects/${projectId}/rls`, icon: ShieldCheckIcon },
    ],
  },
  {
    label: 'Operations',
    items: [
      { name: 'Backups', href: `/projects/${projectId}/backups`, icon: ArchiveBoxIcon },
      { name: 'Read replica', href: `/projects/${projectId}/replicas`, icon: ServerStackIcon },
      { name: 'PITR', href: `/projects/${projectId}/pitr`, icon: ClockIcon },
      { name: 'Monitoring', href: `/projects/${projectId}/monitoring`, icon: ChartBarIcon },
      { name: 'Real-time', href: `/projects/${projectId}/realtime`, icon: BoltIcon },
    ],
  },
  {
    label: 'Settings',
    items: [
      { name: 'Settings', href: `/projects/${projectId}/settings`, icon: Cog6ToothIcon },
    ],
  },
];

function isNavActive(itemName: string, itemHref: string, pathname: string): boolean {
  if (itemName === 'Overview') {
    return pathname === itemHref;
  }
  return pathname.startsWith(itemHref);
}

function ProjectSidebar({ projectId }: { projectId: string }) {
  const location = useLocation();
  const navGroups = getProjectNavGroups(projectId);

  const { data: projectResponse } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId),
    { staleTime: 30000 }
  );

  const project = projectResponse?.data?.project;

  return (
    <div className="flex flex-col h-full">
      {/* Project header */}
      <div className="px-4 py-5 border-b border-gray-200">
        <Link
          to="/projects"
          className="inline-flex items-center text-xs text-gray-400 hover:text-gray-600 mb-3"
        >
          <ArrowLeftIcon className="h-3 w-3 mr-1" />
          All Projects
        </Link>
        <h2 className="text-sm font-semibold text-gray-900 truncate">
          {project?.name || 'Loading...'}
        </h2>
        {project && (
          <div className="mt-1 flex items-center">
            {project.status === 'active' ? (
              <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 mr-1" />
            ) : (
              <ExclamationTriangleIcon className="h-3.5 w-3.5 text-yellow-500 mr-1" />
            )}
            <span className={clsx(
              'text-xs',
              project.status === 'active' ? 'text-green-600' : 'text-yellow-600'
            )}>
              {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
            </span>
          </div>
        )}
      </div>

      {/* Navigation groups */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {navGroups.map((group) => (
          <div key={group.label}>
            <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {group.label}
            </p>
            <div className="mt-2 space-y-1">
              {group.items.map((item) => {
                const active = isNavActive(item.name, item.href, location.pathname);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={clsx(
                      active
                        ? 'bg-primary-50 text-primary-700 border-primary-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-transparent',
                      'group flex items-center px-3 py-2 text-sm font-medium rounded-md border-l-2 transition-colors'
                    )}
                  >
                    <item.icon
                      className={clsx(
                        active ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500',
                        'mr-3 h-4 w-4 flex-shrink-0'
                      )}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );
}

function TopBar({ showMobileMenuButton, onMobileMenuToggle }: {
  showMobileMenuButton: boolean;
  onMobileMenuToggle: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const isProjectContext = location.pathname.startsWith('/projects/') && location.pathname.split('/').length > 2;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
      <div className={clsx(
        'flex h-14 items-center justify-between px-4',
        !isProjectContext && 'mx-auto max-w-7xl sm:px-6 lg:px-8'
      )}>
        <div className="flex items-center">
          {showMobileMenuButton && (
            <button
              onClick={onMobileMenuToggle}
              className="mr-3 p-1.5 text-gray-400 hover:text-gray-600 lg:hidden"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          )}
          <Link to="/dashboard" className="flex items-center">
            <span className="text-xl font-bold text-primary-600">Air Raid</span>
          </Link>

          {/* Main nav links (only when NOT in project context) */}
          {!isProjectContext && (
            <nav className="hidden sm:ml-8 sm:flex sm:space-x-6">
              {mainNavigation.map((item) => {
                const active = location.pathname.startsWith(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={clsx(
                      active
                        ? 'text-gray-900 border-primary-500'
                        : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300',
                      'inline-flex items-center border-b-2 px-1 pt-1 pb-0.5 text-sm font-medium'
                    )}
                  >
                    <item.icon className="mr-1.5 h-4 w-4" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>

        {/* User menu */}
        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
            <span className="sr-only">Open user menu</span>
            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
              <UserIcon className="h-4 w-4 text-primary-600" />
            </div>
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="px-4 py-2 text-sm text-gray-700 border-b">
                <div className="font-medium truncate">{user?.name || 'User'}</div>
                <div className="text-gray-500 text-xs truncate">{user?.email}</div>
              </div>
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/profile"
                    className={clsx(active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700')}
                  >
                    Profile Settings
                  </Link>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/developer"
                    className={clsx(active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700')}
                  >
                    <CodeBracketIcon className="inline mr-2 h-4 w-4" />
                    Developer Tools
                  </Link>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleLogout}
                    className={clsx(active ? 'bg-gray-100' : '', 'block w-full text-left px-4 py-2 text-sm text-gray-700')}
                  >
                    <ArrowRightStartOnRectangleIcon className="inline mr-2 h-4 w-4" />
                    Sign out
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Extract project ID from URL
  const pathParts = location.pathname.split('/');
  const projectId = pathParts[1] === 'projects' && pathParts[2] && pathParts[2] !== 'new' ? pathParts[2] : null;
  const isProjectContext = !!projectId;

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar
        showMobileMenuButton={isProjectContext}
        onMobileMenuToggle={() => setMobileMenuOpen(true)}
      />

      {isProjectContext ? (
        // Project context: sidebar + content
        <div className="flex min-w-0">
          {/* Desktop sidebar */}
          <aside className="hidden lg:flex lg:flex-col lg:w-60 lg:fixed lg:inset-y-0 lg:top-14 bg-white border-r border-gray-200">
            <ProjectSidebar projectId={projectId} />
          </aside>

          {/* Mobile sidebar */}
          <Transition show={mobileMenuOpen} as={Fragment}>
            <Dialog onClose={() => setMobileMenuOpen(false)} className="relative z-40 lg:hidden">
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-600/50" />
              </Transition.Child>
              <div className="fixed inset-0 flex">
                <Transition.Child
                  as={Fragment}
                  enter="transition ease-in-out duration-200 transform"
                  enterFrom="-translate-x-full"
                  enterTo="translate-x-0"
                  leave="transition ease-in-out duration-200 transform"
                  leaveFrom="translate-x-0"
                  leaveTo="-translate-x-full"
                >
                  <Dialog.Panel className="relative flex w-60 flex-col bg-white shadow-xl">
                    <div className="absolute right-0 top-0 -mr-12 pt-2">
                      <button
                        onClick={() => setMobileMenuOpen(false)}
                        className="ml-1 flex h-10 w-10 items-center justify-center rounded-full text-white hover:text-gray-300"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <div onClick={() => setMobileMenuOpen(false)}>
                      <ProjectSidebar projectId={projectId} />
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>

          {/* Main content area */}
          <main className="min-w-0 flex-1 lg:pl-60">
            <div className={clsx(
              'min-w-0 py-8 px-4 sm:px-6 lg:px-8',
              location.pathname.endsWith('/query-builder') ? 'max-w-full' : 'max-w-6xl'
            )}>
              {children}
            </div>
          </main>
        </div>
      ) : (
        // Non-project context: simple centered content
        <main className="py-10">
          <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      )}
    </div>
  );
}
